/**
 * 💰🎁🕰️ Currency Vouchers Panel (Deanah & Muki canon · Feb 2026)
 *
 * Renders the player's Echoing Pouches / Aeon's Caches / Chronophage Palettes
 * with a tap-to-redeem button, plus a display-only Ledger stub if owned.
 *
 * Backed by:
 *   GET  /api/currency-vouchers/inventory
 *   POST /api/currency-vouchers/redeem  { item_id, qty? }
 *
 * MVP behavior:
 *   • Vouchers only enter inventory via owner grant (no drops yet).
 *   • Redeem converts to Chits atomically via chits_service.grant().
 *   • Ledger is a lore stub — shown when owned, no interaction available.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import MoneyEggCrackModal from "./MoneyEggCrackModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tailwind color themes keyed off the backend's `color` hint per catalog row.
const COLOR_THEME = {
  indigo:   { border: "border-indigo-500/40", chip: "bg-indigo-950/60 text-indigo-200", glow: "shadow-indigo-900/40", accent: "text-indigo-300" },
  violet:   { border: "border-violet-500/50", chip: "bg-violet-950/60 text-violet-200", glow: "shadow-violet-900/50", accent: "text-violet-300" },
  amber:    { border: "border-amber-500/50", chip: "bg-amber-950/60 text-amber-200", glow: "shadow-amber-900/50", accent: "text-amber-300" },
  pink:     { border: "border-pink-500/50",  chip: "bg-pink-950/60 text-pink-200",     glow: "shadow-pink-900/50",   accent: "text-pink-300"   },
};
const DEFAULT_THEME = COLOR_THEME.indigo;

function formatChits(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return `${v}`;
}

export default function CurrencyVouchersPanel() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [inventory, setInventory] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [redeemAllBusy, setRedeemAllBusy] = useState(false);
  const [eggModalOpen, setEggModalOpen] = useState(false);
  const [fragmentMap, setFragmentMap] = useState({});  // item_id → {fragment_id, number, book_id}

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/currency-vouchers/inventory`);
      const cat = r.data?.catalog || [];
      setCatalog(cat);
      setInventory(r.data?.inventory || {});
      // Lookup Paradox Diaries fragments bound to each voucher item (silent fail)
      const lookups = await Promise.all(
        cat.map((c) =>
          axios.get(`${API}/paradox-diaries/for-item/${c.item_id}`)
            .then((rr) => [c.item_id, rr.data?.fragment || null])
            .catch(() => [c.item_id, null])
        )
      );
      const map = {};
      lookups.forEach(([iid, frag]) => { if (frag) map[iid] = frag; });
      setFragmentMap(map);
    } catch (e) {
      // Component stays hidden if endpoint fails
      setCatalog([]);
      setInventory({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onRedeem = async (item) => {
    const owned = Number(inventory[item.item_id] || 0);
    if (owned <= 0) return;
    setBusyId(item.item_id);
    try {
      const r = await axios.post(`${API}/currency-vouchers/redeem`, {
        item_id: item.item_id,
        qty: 1,
      });
      const credited = r.data?.redeemed?.chits_credited || 0;
      toast.success(`${item.icon} +${credited.toLocaleString()} Chits`, {
        description: r.data?.flavor,
        duration: 4500,
      });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeem failed.");
    } finally {
      setBusyId(null);
    }
  };

  // 🎯 "Use All" — one-tap redeem every fixed-value voucher (+ crack any
  // Money Eggs the user owns). Confirms first so RNG eggs aren't opened
  // by accident.
  const onRedeemAll = async () => {
    const eggCount = Number(inventory.cl_money_egg || 0);
    const fixedStacks = catalog.filter(
      (c) => c.kind === "currency_voucher" && Number(inventory[c.item_id] || 0) > 0
    );
    if (fixedStacks.length === 0 && eggCount === 0) return;
    const eggWarning = eggCount > 0
      ? `\n\nAlso cracks ${eggCount} Money Egg${eggCount === 1 ? "" : "s"} (RNG — no reveal animation).`
      : "";
    const ok = window.confirm(
      `Redeem ALL of the following?\n\n` +
      fixedStacks
        .map((c) => `${c.icon} ${c.name} × ${inventory[c.item_id]}`)
        .join("\n") +
      eggWarning
    );
    if (!ok) return;
    setRedeemAllBusy(true);
    try {
      const r = await axios.post(`${API}/currency-vouchers/redeem-all`, {
        crack_money_eggs: true,
      });
      const total = r.data?.chits_credited || 0;
      const rows = r.data?.redeemed || [];
      const eggs = r.data?.money_egg_rolls || [];
      const failures = r.data?.failures || [];
      const eggChits = eggs
        .filter((e) => e.currency === "chits")
        .reduce((s, e) => s + Number(e.amount || 0), 0);
      const rolledOther = eggs
        .filter((e) => e.currency !== "chits")
        .map((e) => `+${e.amount} ${e.currency.replace("_", " ")}`)
        .join(" · ");
      toast.success(
        `🎯 Cashed out ${rows.length} voucher${rows.length === 1 ? "" : " stacks"}${eggs.length ? ` + ${eggs.length} egg${eggs.length === 1 ? "" : "s"}` : ""}`,
        {
          description: `+${(total + eggChits).toLocaleString()} Chits credited${rolledOther ? ` · ${rolledOther}` : ""}${failures.length ? ` · ${failures.length} failed` : ""}`,
          duration: 6000,
        }
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Use All failed.");
    } finally {
      setRedeemAllBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-xs text-neutral-500 italic" data-testid="currency-vouchers-loading">
        Loading vouchers…
      </div>
    );
  }

  // Only show the panel if the user owns at least one of anything OR the endpoint
  // returned a non-empty catalog and they want to preview what's coming.
  const owned = catalog.filter((c) => Number(inventory[c.item_id] || 0) > 0);
  if (owned.length === 0) {
    return (
      <div className="rounded-xl border border-neutral-800 bg-black/40 p-4 text-center" data-testid="currency-vouchers-empty">
        <div className="text-3xl mb-1">💰 🎁 🕰️</div>
        <div className="text-xs text-neutral-400 leading-relaxed">
          <span className="italic">Deanah &amp; Muki</span> haven&apos;t sent any timeline-fractured currencies your way yet.
          <br />
          Their diary — <span className="text-amber-300 font-semibold">The Chrono-Chronicler&apos;s Ledger</span> — chronicles three impossible currencies waiting to be found.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="currency-vouchers-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-2">
          <span className="text-amber-300">📘</span>
          <span>Unsanctioned Currencies · signed by 𝓓𝓮𝓪𝓷𝓪𝓱 &amp; 𝓜𝓾𝓴𝓲</span>
        </div>
        <button
          type="button"
          onClick={onRedeemAll}
          disabled={redeemAllBusy || busyId != null}
          data-testid="currency-vouchers-use-all"
          className={`px-3 py-1.5 rounded-md text-[11px] font-black uppercase tracking-widest border transition ${
            redeemAllBusy || busyId != null
              ? "border-neutral-800 bg-neutral-900 text-neutral-600 cursor-not-allowed"
              : "border-amber-500/70 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:text-amber-100"
          }`}
          title="Redeem every unsanctioned currency you own — fixed-value vouchers roll into Chits, Money Eggs are auto-cracked."
        >
          {redeemAllBusy ? "Cashing out…" : "🎯 Use All"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {catalog.map((item) => {
          const count = Number(inventory[item.item_id] || 0);
          if (count <= 0) return null;
          const theme = COLOR_THEME[item.color] || DEFAULT_THEME;
          const isLedger = item.kind === "lore_stub";
          const isMoneyEgg = item.kind === "money_egg_rng";
          const isBusy = busyId === item.item_id;

          return (
            <div
              key={item.item_id}
              data-testid={`voucher-card-${item.item_id}`}
              className={`relative rounded-xl border ${theme.border} bg-gradient-to-br from-black/70 to-neutral-950 p-4 shadow-lg ${theme.glow}`}
            >
              <div className="flex items-start gap-3">
                <div className={`text-4xl leading-none select-none ${isMoneyEgg ? "animate-pulse" : ""}`} aria-hidden>
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className={`font-black text-sm ${theme.accent} truncate`} style={{ fontFamily: "Anton, sans-serif" }}>
                      {item.name.toUpperCase()}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${theme.chip}`}>
                      ×{count}
                    </span>
                  </div>
                  {!isLedger && !isMoneyEgg && (
                    <div className="text-[11px] text-neutral-300 mt-1">
                      Redeems for <span className="font-black text-amber-300">🪙 {item.chit_value.toLocaleString()}</span> Chits each
                    </div>
                  )}
                  {isMoneyEgg && (
                    <div className="text-[11px] text-neutral-300 mt-1">
                      🎲 Rolls <span className="font-black text-pink-300">1–250</span> of a random currency
                    </div>
                  )}
                  <div className="text-[10px] text-neutral-500 italic mt-1 line-clamp-3">
                    {item.flavor}
                  </div>
                  {item.warning && (
                    <div className="text-[10px] text-red-400/80 mt-1 font-semibold">
                      ⚠️ {item.warning}
                    </div>
                  )}
                  {fragmentMap[item.item_id] && (
                    <a
                      href={`/paradox-diaries?fragment=${fragmentMap[item.item_id].fragment_id}`}
                      data-testid={`voucher-fragment-link-${item.item_id}`}
                      className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-400 px-2 py-0.5 rounded-full font-mono transition"
                    >
                      📑 Read Fragment #{String(fragmentMap[item.item_id].number || 0).padStart(3, "0")}
                    </a>
                  )}
                </div>
              </div>

              {isLedger ? (
                <div className="mt-3 text-center text-[10px] text-neutral-500 italic border-t border-neutral-800 pt-2" data-testid={`voucher-locked-${item.item_id}`}>
                  📖 Lore item — mechanics arriving in a future patch.
                </div>
              ) : isMoneyEgg ? (
                <button
                  onClick={() => setEggModalOpen(true)}
                  data-testid={`voucher-crack-${item.item_id}`}
                  className="mt-3 w-full py-2 rounded-lg text-xs font-black tracking-wider transition
                    bg-gradient-to-r from-pink-600 via-fuchsia-600 to-blue-700 hover:from-pink-500 hover:via-fuchsia-500 hover:to-blue-600 text-white
                    shadow-lg shadow-pink-500/30"
                >
                  💥 CRACK THE EGG
                </button>
              ) : (
                <button
                  onClick={() => onRedeem(item)}
                  disabled={isBusy || count <= 0}
                  data-testid={`voucher-redeem-${item.item_id}`}
                  className={`mt-3 w-full py-2 rounded-lg text-xs font-black tracking-wider transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    item.color === "amber"
                      ? "bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-400 hover:to-amber-600 text-black"
                      : item.color === "violet"
                        ? "bg-gradient-to-r from-violet-600 to-violet-800 hover:from-violet-500 hover:to-violet-700 text-white"
                        : "bg-gradient-to-r from-indigo-600 to-indigo-800 hover:from-indigo-500 hover:to-indigo-700 text-white"
                  }`}
                >
                  {isBusy ? "⏳ Redeeming…" : `🪙 REDEEM · +${formatChits(item.chit_value)} Chits`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <MoneyEggCrackModal
        open={eggModalOpen}
        onClose={() => setEggModalOpen(false)}
        ownedCount={Number(inventory.cl_money_egg || 0)}
        onCrackComplete={(data) => {
          if (data?.inventory) setInventory(data.inventory);
        }}
      />
    </div>
  );
}
